import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// Amplify is configured in services/amplifyConfig.js — import it to ensure config runs early
import './services/amplifyConfig';
import Navigation from './components/Navigation';
import InvoiceCapture from './components/InvoiceCapture';

import Dashboard from './components/Dashboard';
import ExtractionJobsList from './components/ExtractionJobsList';
import ExtractionJobDetail from './components/ExtractionJobDetail';
import CustomerManagementPage from './components/customer-management/CustomerManagementPage';
import { ToastProvider } from './contexts/ToastContext';
import UserManagement from './components/UserManagement';
import RoleManagement from './components/RoleManagement';
import AuditHistory from './components/AuditHistory';
import AugmentationJobDetail from './components/AugmentationJobDetail';
import ApprovedModelsPage from './pages/ApprovedModelsPage';
import ModelConfigurationPage from './pages/ModelConfigurationPage';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PermissionRoute from './components/auth/PermissionRoute';
import AccessDenied from './components/AccessDenied';
import { AuthProvider } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import BreadcrumbBar from './components/shared/BreadcrumbBar';
import SilentErrorBoundary from './components/shared/SilentErrorBoundary';
import './App.css';

// Lazy load ReportingDashboard for code splitting
const ReportingDashboard = lazy(() => import('./pages/ReportingDashboard'));

// Configure React Query client for optimal caching of reporting data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <NavigationProvider>
          <BreadcrumbProvider>
          <ToastProvider>
            <Router>
            <Navigation />
            <SilentErrorBoundary>
              <BreadcrumbBar />
            </SilentErrorBoundary>
            <Container fluid className="mt-4 mb-5 px-4">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/capture" element={
                  <ProtectedRoute>
                    <PermissionRoute resource="invoices" action="analyze">
                      <InvoiceCapture />
                    </PermissionRoute>
                  </ProtectedRoute>
                } />

                <Route path="/extraction-jobs" element={
                  <ProtectedRoute>
                    <ExtractionJobsList />
                  </ProtectedRoute>
                } />
                <Route path="/extraction-jobs/:jobId" element={
                  <ProtectedRoute>
                    <ExtractionJobDetail />
                  </ProtectedRoute>
                } />
                <Route path="/customers/*" element={
                  <ProtectedRoute>
                    <PermissionRoute resource="customers" action="manage">
                      <CustomerManagementPage />
                    </PermissionRoute>
                  </ProtectedRoute>
                } />
                <Route path="/users" element={
                  <ProtectedRoute>
                    <PermissionRoute resource="permissions" action="manage">
                      <UserManagement />
                    </PermissionRoute>
                  </ProtectedRoute>
                } />
                <Route path="/roles" element={
                  <ProtectedRoute>
                    <PermissionRoute resource="permissions" action="manage">
                      <RoleManagement />
                    </PermissionRoute>
                  </ProtectedRoute>
                } />
                <Route path="/audit" element={
                  <ProtectedRoute>
                    <PermissionRoute resource="permissions" action="manage">
                      <AuditHistory />
                    </PermissionRoute>
                  </ProtectedRoute>
                } />
              <Route path="/admin/approved-models" element={
                <ProtectedRoute>
                  <PermissionRoute resource="approved_models" action="view">
                    <ApprovedModelsPage />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
              <Route path="/admin/model-configuration" element={
                <ProtectedRoute>
                  <PermissionRoute resource="model_config" action="view">
                    <ModelConfigurationPage />
                  </PermissionRoute>
                </ProtectedRoute>
              } />
                <Route path="/augmentation-jobs/:jobId" element={
                  <ProtectedRoute>
                    <AugmentationJobDetail />
                  </ProtectedRoute>
                } />
                <Route path="/reports" element={
                  <ProtectedRoute>
                    <PermissionRoute resource="reports" action="view">
                      <Suspense fallback={<div>Loading...</div>}>
                        <ReportingDashboard />
                      </Suspense>
                    </PermissionRoute>
                  </ProtectedRoute>
                } />
                <Route path="/access-denied" element={<AccessDenied />} />
              </Routes>
            </Container>
            </Router>
          </ToastProvider>
          </BreadcrumbProvider>
        </NavigationProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;